import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShowToast } from '@/hooks';
import { useValidateEsignDocumentsMutation } from '@/store/query/interview-plan';
import {
  ESIGN_ACTIVITY_COPY,
  ESIGN_DOCUMENT_LIMITS,
} from '@/constants/esignature';
import type {
  EsignActivityDocument,
  EsignValidateDocumentsResponse,
  EsignWizardDocument,
} from '@/types/esignature';
import {
  getDocumentsBlocker,
  mergeValidation,
  releaseDocumentUrl,
  toWizardDocumentFromFile,
  toWizardDocumentFromSaved,
} from './esignDocuments';

interface UseEsignDocumentsArgs {
  jobId?: string | number;
  stageId?: string | number;
  /** Documents already on the activity, when editing. */
  savedDocuments?: EsignActivityDocument[];
}

const reorder = <T>(items: T[], from: number, to: number): T[] => {
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * The wizard's document list: add, validate, remove, reorder.
 *
 * Validation is stateless on the server and runs per batch of newly added
 * files, so re-validating the whole list on every add would re-upload documents
 * that already have a verdict. Only the new files are sent, and their results
 * are merged back by filename.
 */
export const useEsignDocuments = ({
  jobId,
  stageId,
  savedDocuments,
}: UseEsignDocumentsArgs) => {
  const toast = useShowToast();
  const [documents, setDocuments] = useState<EsignWizardDocument[]>(() =>
    (savedDocuments ?? []).map(toWizardDocumentFromSaved)
  );
  const [validateDocuments, { isLoading: isValidating }] =
    useValidateEsignDocumentsMutation();

  // The activity is fetched, so on an edit the saved documents arrive a render
  // or two after mount — the useState initializer above has already run against
  // an empty list by then, which would leave the wizard showing no documents
  // for an activity that has them. Re-seed whenever they change identity, but
  // stop once the recruiter has touched the list so a refetch cannot silently
  // discard their edits. This also covers a cloned activity, whose documents
  // are deep-copied by a worker and land empty on the first read.
  //
  // Adjusted during render rather than in an effect: React re-runs this
  // component immediately with the new state, so the list is never painted
  // empty first.
  const isDirtyRef = useRef(false);
  const [seededFrom, setSeededFrom] = useState(savedDocuments);
  if (!isDirtyRef.current && savedDocuments !== seededFrom) {
    setSeededFrom(savedDocuments);
    setDocuments((savedDocuments ?? []).map(toWizardDocumentFromSaved));
  }

  const addFiles = useCallback(
    async (files: File[]) => {
      if (!files?.length) return;

      const room = ESIGN_DOCUMENT_LIMITS.MAX_FILES - documents.length;
      if (room <= 0) {
        toast({ title: ESIGN_ACTIVITY_COPY.COUNT_LIMIT, status: 'error' });
        return;
      }
      // Take what fits rather than rejecting the whole drop — the recruiter
      // keeps the documents they can have and is told why the rest were left.
      const accepted = files.slice(0, room);
      if (accepted.length < files.length) {
        toast({ title: ESIGN_ACTIVITY_COPY.COUNT_LIMIT, status: 'error' });
      }

      const added = accepted.map(toWizardDocumentFromFile);
      isDirtyRef.current = true;
      setDocuments((current) => [...current, ...added]);

      const response = await validateDocuments({
        payload: { jobId, stageId, files: accepted },
      });

      if ('error' in response) {
        toast({ title: ESIGN_ACTIVITY_COPY.VALIDATE_ERROR, status: 'error' });
        // Drop the rows we could not get a verdict for: leaving them unvalidated
        // would let a document with unknown tokens through the Next gate, since
        // the gate only blocks on a known-bad verdict.
        const addedKeys = new Set(added.map((document) => document.key));
        setDocuments((current) =>
          current.filter((document) => !addedKeys.has(document.key))
        );
        return;
      }

      const results =
        (response.data as EsignValidateDocumentsResponse)?.documents ?? [];
      setDocuments((current) => mergeValidation(current, results));
    },
    [documents.length, jobId, stageId, toast, validateDocuments]
  );

  const removeDocument = useCallback((key: string) => {
    isDirtyRef.current = true;
    setDocuments((current) => {
      releaseDocumentUrl(current.find((document) => document.key === key));
      return current.filter((document) => document.key !== key);
    });
  }, []);

  const reorderDocuments = useCallback((from: number, to: number) => {
    isDirtyRef.current = true;
    setDocuments((current) => reorder(current, from, to));
  }, []);

  // Object URLs live until revoked, so a wizard opened and closed repeatedly
  // would otherwise leak one blob per uploaded file for the rest of the session.
  const documentsRef = useRef(documents);
  documentsRef.current = documents;
  useEffect(
    () => () => {
      documentsRef.current.forEach(releaseDocumentUrl);
    },
    []
  );

  const blocker = useMemo(() => getDocumentsBlocker(documents), [documents]);

  return {
    documents,
    isValidating,
    blocker,
    canContinue: blocker === null && !isValidating,
    addFiles,
    removeDocument,
    reorderDocuments,
  };
};
